<template>
    <div class="trip-stops-editor">
        <draggable v-model="stops" handle=".trip-stops-editor__handle" :disabled="disabled" @end="emitChange">
            <div v-for="(stop, index) in stops" :key="stop.key" class="trip-stops-editor__row">
                <v-icon class="trip-stops-editor__handle" :disabled="disabled" title="Arraste para reordenar">
                    mdi-drag-vertical
                </v-icon>

                <span class="trip-stops-editor__badge">{{ index + 1 }}</span>

                <div class="trip-stops-editor__fields">
                    <v-autocomplete dense outlined hide-details="auto" return-object :filter="() => true"
                        :disabled="disabled" :label="`Parada ${index + 1}`" :items="itemsFor(stop)"
                        item-text="address" item-value="address" :value="stop.address && stop.address.address ? stop.address : null"
                        :loading="!!loading[stop.key]" :rules="[v => !!v || 'Informe o endereço da parada']"
                        placeholder="Digite ao menos 3 caracteres" no-data-text="Nenhum endereço encontrado"
                        @update:search-input="val => search(stop, val)" @change="val => select(stop, val)">
                        <template v-slot:item="{ item }">
                            <div class="py-2">
                                <span v-if="item.name" class="text-caption"><strong>{{ item.name }}</strong><br /></span>
                                <span class="text-caption">{{ item.address }}</span>
                            </div>
                        </template>
                        <template v-slot:selection="{ item }">
                            <span class="text-body-2 text-truncate" :title="item.address">{{ item.address }}</span>
                        </template>
                    </v-autocomplete>

                    <v-text-field dense outlined hide-details="auto" class="mt-2" label="Observação (opcional)"
                        maxlength="255" :disabled="disabled" v-model="stop.observation" @input="emitChange">
                    </v-text-field>

                    <div v-if="showArrival && stop.arrived_at" class="trip-stops-editor__arrival">
                        <v-icon x-small color="success">mdi-check-circle</v-icon>
                        Chegada registrada em {{ formatDateTime(stop.arrived_at) }}
                    </div>
                </div>

                <v-btn icon small color="error" :disabled="disabled" title="Remover parada" @click="remove(index)">
                    <v-icon small>mdi-delete-outline</v-icon>
                </v-btn>
            </div>
        </draggable>

        <div v-if="!stops.length" class="text-body-2 grey--text mb-2">Nenhuma parada adicionada.</div>

        <v-btn small outlined color="primary" :disabled="disabled || stops.length >= max" @click="add">
            <v-icon small left>mdi-map-marker-plus-outline</v-icon>
            Adicionar parada
        </v-btn>
        <span class="text-caption grey--text ml-2">{{ stops.length }}/{{ max }}</span>
    </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import draggable from 'vuedraggable'

let uid = 0

const newToken = () => (window.crypto && window.crypto.randomUUID)
    ? window.crypto.randomUUID()
    : `${Date.now()}${Math.random()}`

// Lista editável de pontos de parada (endereço + observação), reordenável.
// v-model: [{ id?, coords: [lat, lng], address: { iid, address, city, state, district }, observation, arrived_at? }]
export default {
    name: 'TripStopsEditor',
    components: { draggable },
    props: {
        value: { type: Array, default: () => [] },
        max: { type: Number, default: 5 },
        disabled: { type: Boolean, default: false },
        showArrival: { type: Boolean, default: false }
    },
    data: () => ({
        stops: [],
        results: {},
        loading: {},
        tokens: {},
        timers: {},
        controllers: {}
    }),
    watch: {
        value: {
            immediate: true,
            handler(value) {
                // Ignora o eco do próprio emit para não recriar as linhas
                if (value === this.lastEmitted) return
                this.stops = (value || []).map(stop => ({
                    ...stop,
                    key: stop.key || `stop-${++uid}`,
                    address: { ...(stop.address || {}) },
                    observation: stop.observation || ''
                }))
            }
        }
    },
    methods: {
        emitChange() {
            this.lastEmitted = this.stops.map(stop => ({ ...stop }))
            this.$emit('input', this.lastEmitted)
        },
        add() {
            if (this.stops.length >= this.max) return
            this.stops.push({ key: `stop-${++uid}`, coords: null, address: {}, observation: '' })
            this.emitChange()
        },
        remove(index) {
            this.stops.splice(index, 1)
            this.emitChange()
        },
        itemsFor(stop) {
            const list = [...(this.results[stop.key] || [])]
            // A seleção atual precisa estar entre os itens para aparecer no campo
            if (stop.address && stop.address.address && !list.some(item => item.address === stop.address.address)) {
                list.unshift(stop.address)
            }
            return list
        },
        search(stop, value) {
            clearTimeout(this.timers[stop.key])

            if (!value || value.trim().length < 3 || (stop.address && value === stop.address.address)) return

            this.timers[stop.key] = setTimeout(() => this.runSearch(stop, value), 500)
        },
        async runSearch(stop, value) {
            if (!this.tokens[stop.key]) this.tokens[stop.key] = newToken()
            if (this.controllers[stop.key]) this.controllers[stop.key].abort()

            this.controllers[stop.key] = new AbortController()
            this.$set(this.loading, stop.key, true)

            try {
                const response = await axios.get(`/api/v2/google-maps/find/${encodeURIComponent(value)}`, {
                    params: { sessiontoken: this.tokens[stop.key] },
                    signal: this.controllers[stop.key].signal
                })

                this.$set(this.results, stop.key, (response.data.content || []).map(item => ({
                    name: item.name,
                    address: item.address,
                    iid: item.placeId,
                    lat: item.lat,
                    lng: item.lng
                })))
            } catch (error) {
                if (!axios.isCancel(error)) console.error('Erro ao buscar endereço da parada:', error)
            } finally {
                this.$set(this.loading, stop.key, false)
            }
        },
        async select(stop, item) {
            if (!item) {
                stop.address = {}
                stop.coords = null
                this.emitChange()
                return
            }

            if (item.iid === stop.address.iid && stop.coords) return

            this.$set(this.loading, stop.key, true)

            try {
                const response = await axios.get(`/api/v2/google-maps/place-details/${item.iid}`, {
                    params: this.tokens[stop.key] ? { sessiontoken: this.tokens[stop.key] } : {}
                })
                const details = response.data.content || {}

                stop.address = {
                    iid: item.iid,
                    address: item.address,
                    city: details.city || null,
                    state: details.state || null,
                    district: details.district || null
                }
                stop.coords = [details.coordinates.lat, details.coordinates.lng]
                // Endereço mudou: a chegada registrada não vale mais para o novo ponto
                stop.arrived_at = null
                this.tokens[stop.key] = null
                this.emitChange()
            } catch (error) {
                console.error('Erro ao buscar detalhes do endereço da parada:', error)
                this.$emit('error', 'Não foi possível obter as coordenadas do endereço selecionado')
            } finally {
                this.$set(this.loading, stop.key, false)
            }
        },
        formatDateTime(value) {
            return moment(value).format('DD/MM/YYYY HH:mm')
        }
    }
}
</script>

<style lang="scss">
.trip-stops-editor__row {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 8px 0;
}

.trip-stops-editor__handle {
    cursor: grab;
    margin-top: 6px;
}

.trip-stops-editor__badge {
    flex: 0 0 24px;
    height: 24px;
    margin-top: 6px;
    border-radius: 50%;
    background: #f57c00;
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
}

.trip-stops-editor__fields {
    flex: 1 1 auto;
    min-width: 0;
}

.trip-stops-editor__arrival {
    margin-top: 4px;
    font-size: 12px;
    color: #2e7d32;
}
</style>
